import { useCallback, useMemo, useState } from 'react';
import { type Control, type Resolver, useForm } from 'react-hook-form';
import type { StepFooterConfig } from '@components/ui/SteppedModal';
import { zodResolver } from '@hookform/resolvers/zod';

import type { AddonOnboardingConfig, OnboardingStepConfig, OnboardingStepKind } from '../types';

import { buildOnboardingSchema } from './buildOnboardingSchema';

interface UseAddonOnboardingOptions {
  config: AddonOnboardingConfig;
  /** Seed values (e.g. the current user's email) merged over the empty form defaults. */
  initialValues?: Record<string, string>;
  /**
   * Persist a form step's captured values on advance (e.g. the proxy-registration
   * mutation). Runs only after the step's fields pass validation. If it rejects,
   * the flow stays on the step and surfaces the error.
   */
  onSubmit?: (values: Record<string, string>) => Promise<void> | void;
  /** Called when the user finishes the last step. Receives captured form values. */
  onComplete?: (values: Record<string, string>) => void;
  /** Called when the user dismisses the flow. */
  onClose?: () => void;
  /** Called when the visible step changes (next/back), reporting the step navigated to. */
  onStepChange?: (info: {
    stepIndex: number;
    stepKind: OnboardingStepKind;
    direction: 'next' | 'back';
  }) => void;
}

interface UseAddonOnboardingResult {
  stepIndex: number;
  currentStep: OnboardingStepConfig;
  totalSteps: number;
  /** react-hook-form control for the (single, flow-wide) form. */
  control: Control;
  footer: StepFooterConfig;
  isSubmitting: boolean;
  submitError: string | null;
  handleClose: () => void;
}

/** Empty-string defaults for every form field, seeded by `initialValues`. */
function buildDefaults(
  config: AddonOnboardingConfig,
  seed?: Record<string, string>,
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const step of config.steps) {
    if (step.kind === 'form') {
      for (const field of step.form.fields) {
        values[field.name] = seed?.[field.name] ?? '';
      }
    }
  }
  return values;
}

/**
 * Step state machine for a config-driven onboarding flow, backed by a single
 * react-hook-form instance (validation via a runtime zod schema). Owns step
 * navigation and submission; the form fields render against the returned
 * `control`. Advancing past a form step validates just that step's fields, then
 * runs `onSubmit` (loading + error state) and advances on success.
 */
export function useAddonOnboarding({
  config,
  initialValues,
  onSubmit,
  onComplete,
  onClose,
  onStepChange,
}: UseAddonOnboardingOptions): UseAddonOnboardingResult {
  const [stepIndex, setStepIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const schema = useMemo(() => buildOnboardingSchema(config), [config]);
  const defaultValues = useMemo(
    () => buildDefaults(config, initialValues),
    [config, initialValues],
  );

  const form = useForm({
    resolver: zodResolver(schema) as Resolver,
    defaultValues,
    mode: 'onTouched',
  });

  const totalSteps = config.steps.length;
  const currentStep = config.steps[stepIndex];
  const isLastStep = stepIndex === totalSteps - 1;

  const advance = useCallback(() => {
    if (isLastStep) {
      onComplete?.(form.getValues());
      return;
    }
    const nextIndex = Math.min(stepIndex + 1, totalSteps - 1);
    setStepIndex(nextIndex);
    onStepChange?.({
      stepIndex: nextIndex,
      stepKind: config.steps[nextIndex].kind,
      direction: 'next',
    });
  }, [isLastStep, onComplete, form, stepIndex, totalSteps, config.steps, onStepChange]);

  const handleNext = useCallback(async () => {
    // On a form step: validate just this step's fields, then submit, then advance.
    if (currentStep.kind === 'form') {
      const names = currentStep.form.fields.map(field => field.name);
      const valid = await form.trigger(names);
      if (!valid) return;

      if (onSubmit) {
        setSubmitError(null);
        setIsSubmitting(true);
        try {
          await onSubmit(form.getValues());
        } catch (err) {
          setSubmitError(
            err instanceof Error ? err.message : 'Something went wrong. Please try again.',
          );
          return;
        } finally {
          setIsSubmitting(false);
        }
      }
    }
    advance();
  }, [currentStep, form, onSubmit, advance]);

  const handleBack = useCallback(() => {
    setSubmitError(null);
    if (stepIndex === 0) return;
    const prevIndex = stepIndex - 1;
    setStepIndex(prevIndex);
    onStepChange?.({
      stepIndex: prevIndex,
      stepKind: config.steps[prevIndex].kind,
      direction: 'back',
    });
  }, [stepIndex, config.steps, onStepChange]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const footer: StepFooterConfig = {
    nextLabel: isSubmitting
      ? 'Submitting…'
      : (currentStep.nextLabel ?? (isLastStep ? 'Done' : 'Next')),
    onNext: handleNext,
    onBack: handleBack,
    nextDisabled: isSubmitting,
    showBack: !isSubmitting,
    showNext: true,
  };

  return {
    stepIndex,
    currentStep,
    totalSteps,
    control: form.control,
    footer,
    isSubmitting,
    submitError,
    handleClose,
  };
}
